import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { amber, grey } from "@mui/material/colors";
import { federalWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import {
  RcraInfoFederalWasteGeneratorStatus,
  WasteGenerationChartsQuery,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { formatNumber } from "util/formatNumber";

import {
  GeneratorStatusKind,
  getFederalGeneratorStatusColorSettings,
} from "../getFederalGeneratorStatusColorSettings";

type GenerationRow =
  WasteGenerationChartsQuery["facilityWasteGenerationCharts"]["generationByMonth"][number];

type Props = {
  /** Currently-selected month as a yyyy-MM-dd string. */
  selectedMonth: string | null;
  onMonthChange: (month: string) => void;
  data: GenerationRow[];
};

/**
 * Per-month detailed view of the Waste Generated chart — analog of the
 * `MonthDataDetails` card on the Waste Shipments tile. Surfaces:
 *
 *   - A month picker so users can scrub through months without losing context.
 *   - The three-way generator-status comparison (Reported / WADM-Calculated /
 *     Shipment-Calculated) with an explicit divergence callout when the WADM
 *     and shipment sides disagree.
 *   - The federal / state-only / acute series breakdown for the selected month.
 *   - The full per-classification breakdown so users can see *which* WADM
 *     classifications contributed (incl. ones the federal classifier excludes).
 */
export function MonthDetailsView({ selectedMonth, onMonthChange, data }: Props) {
  const monthRow = useMemo(() => {
    if (!selectedMonth) return null;
    return data.find((row) => monthDateToString(row.month) === selectedMonth) ?? null;
  }, [selectedMonth, data]);

  if (!monthRow) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="textSecondary">Select a month to view details</Typography>
      </Box>
    );
  }

  const reportedStatus = monthRow.reportedFederalGeneratorStatus ?? null;
  const wadmStatus = monthRow.calculatedFederalGeneratorStatus ?? null;
  const shipmentStatus = monthRow.shipmentCalculatedFederalGeneratorStatus ?? null;
  const diverges = monthRow.divergesFromShipmentCalculatedStatus;

  return (
    <Box sx={{ p: 3, pt: 1, backgroundColor: grey[100], borderRadius: "4px" }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 280px) 1fr" },
            gap: 3,
            alignItems: "start",
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" color="textPrimary" mb={2}>
              Detailed Month View
            </Typography>
            <FormControl variant="standard" sx={{ width: "100%" }}>
              <InputLabel id="generation-month-select-label">Month</InputLabel>
              <Select
                labelId="generation-month-select-label"
                value={selectedMonth ?? ""}
                label="Month"
                onChange={(event) => onMonthChange(event.target.value as string)}
                sx={{ fontWeight: "bold" }}
              >
                {data.map((row) => {
                  const rawMonth = monthDateToString(row.month);
                  return (
                    <MenuItem key={rawMonth} value={rawMonth}>
                      {formatMonthHeader(rawMonth)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <StatusGrid
            reportedStatus={reportedStatus}
            wadmStatus={wadmStatus}
            shipmentStatus={shipmentStatus}
            diverges={diverges}
          />
        </Box>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
          }}
        >
          <SeriesBreakdown
            federalLbs={monthRow.federalHazardousLbs}
            stateOnlyLbs={monthRow.stateOnlyLbs}
            acuteLbs={monthRow.acuteHazardousLbs}
          />
          <ClassificationBreakdown
            byClassification={[...(monthRow.byClassification ?? [])].sort((a, b) => b.lbs - a.lbs)}
          />
        </Box>
      </Stack>
    </Box>
  );
}

function StatusGrid({
  reportedStatus,
  wadmStatus,
  shipmentStatus,
  diverges,
}: {
  reportedStatus: RcraInfoFederalWasteGeneratorStatus | null;
  wadmStatus: RcraInfoFederalWasteGeneratorStatus | null;
  shipmentStatus: RcraInfoFederalWasteGeneratorStatus | null;
  diverges: boolean;
}) {
  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 1,
        }}
      >
        <StatusCell label="Reported" status={reportedStatus} kind={GeneratorStatusKind.REPORTED} />
        <StatusCell
          label="Calculated (generation)"
          status={wadmStatus}
          kind={GeneratorStatusKind.CALCULATED}
        />
        <StatusCell
          label="Calculated (shipments)"
          status={shipmentStatus}
          kind={GeneratorStatusKind.CALCULATED}
        />
      </Box>
      {diverges && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            backgroundColor: amber[50],
            border: `1px solid ${amber[300]}`,
            borderRadius: 0.5,
            p: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: amber[800], fontSize: "1.25rem", flexShrink: 0 }} />
          <Typography variant="caption" color="textSecondary" sx={{ flex: 1 }}>
            Shipment-derived status (
            <strong>{federalWasteGeneratorStatusToLabel(shipmentStatus)}</strong>) disagrees with
            generation-derived status (
            <strong>{federalWasteGeneratorStatusToLabel(wadmStatus)}</strong>). Heavy shipment
            months can bundle multiple months of accumulated waste, so the generation view is closer
            to real month-by-month status.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

function StatusCell({
  label,
  status,
  kind,
}: {
  label: string;
  status: RcraInfoFederalWasteGeneratorStatus | null;
  kind: GeneratorStatusKind;
}) {
  const colors = getFederalGeneratorStatusColorSettings(status, kind);
  const labelText = status ? federalWasteGeneratorStatusToLabel(status) : "None";
  return (
    <Stack spacing={0.5} minWidth={0}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Box
        sx={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
          color: colors.textColor,
          borderRadius: 0.5,
          px: 1,
          py: 0.5,
          fontSize: "0.875rem",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {labelText}
      </Box>
    </Stack>
  );
}

function SeriesBreakdown({
  federalLbs,
  stateOnlyLbs,
  acuteLbs,
}: {
  federalLbs: number;
  stateOnlyLbs: number;
  acuteLbs: number;
}) {
  return (
    <Box>
      <Typography variant="body2" color="textPrimary" gutterBottom>
        Generation series this month
      </Typography>
      <Stack spacing={0.75}>
        <BreakdownRow label="Federal hazardous" lbs={federalLbs} />
        <BreakdownRow label="State-only" lbs={stateOnlyLbs} />
        <BreakdownRow label="Acute hazardous" lbs={acuteLbs} />
      </Stack>
    </Box>
  );
}

function ClassificationBreakdown({
  byClassification,
}: {
  byClassification: { classification: string; lbs: number }[];
}) {
  return (
    <Box>
      <Typography variant="body2" color="textPrimary" gutterBottom>
        By waste classification this month
      </Typography>
      {byClassification.length === 0 ? (
        <Typography variant="caption" color="textSecondary" fontStyle="italic">
          No WADM rows for this month.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {byClassification.map((entry) => (
            <BreakdownRow key={entry.classification} label={entry.classification} lbs={entry.lbs} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function BreakdownRow({ label, lbs }: { label: string; lbs: number }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2" color="textPrimary" fontWeight={500}>
        {formatNumber(lbs)} lbs
      </Typography>
    </Stack>
  );
}

function formatMonthHeader(date: string): string {
  return DateTime.fromFormat(date, "yyyy-MM-dd").toFormat("LLLL yyyy");
}

function monthDateToString(value: Date | string): string {
  if (typeof value === "string") return value.slice(0, 10);
  return value.toISOString().slice(0, 10);
}
